import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Badge,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  WorkOutline,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axiosInstance from '../api/axiosConfig';
import { getStoredRoles, hasAllowedRole } from '../utils/authSession';

const SectionTitle = ({ title, subtitle, editable, onEdit }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {!editable && onEdit && (
      <Tooltip title={`Edit ${title}`}>
        <IconButton size="small" onClick={onEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const EmployeeEditComponent = () => {
  const [employee, setEmployee] = useState({
    employeeCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    qualification: '',
    experience: '',
    subjectsTaught: '',
    address: '',
    bloodGroup: '',
    salary: '',
    emergencyContact: '',
    dateOfJoining: null,
    role: '',
    status: '',
    imageUrl: '',
  });

  const [loading, setLoading] = useState(true);
  const [isPersonalEditable, setIsPersonalEditable] = useState(false);
  const [isAddressEditable, setIsAddressEditable] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [saving, setSaving] = useState(false);

  const employeeCode = useMemo(() => localStorage.getItem('code'), []);
  const schoolId = useMemo(() => localStorage.getItem('schoolId'), []);
  const userRoles = useMemo(() => getStoredRoles(), []);
  const isAdmin = hasAllowedRole(['SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN'], userRoles);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`/api/employees/employeeCode/${employeeCode}`, {
          headers: { schoolId },
        });

        setEmployee({
          ...response.data,
          dateOfJoining: response.data.dateOfJoining ? dayjs(response.data.dateOfJoining) : null,
        });
      } catch (error) {
        setSnackbarMessage('Failed to load employee data.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    if (employeeCode && schoolId) {
      fetchEmployeeData();
    }
  }, [employeeCode, schoolId]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setEmployee((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDateOfJoiningChange = useCallback((date) => {
    setEmployee((prev) => ({ ...prev, dateOfJoining: date }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const formattedEmployee = {
        ...employee,
        dateOfJoining: employee.dateOfJoining ? employee.dateOfJoining.format('YYYY-MM-DD') : null,
      };

      await axiosInstance.put(`/api/employees/employeeCode/${employeeCode}`, formattedEmployee, {
        headers: { schoolId },
      });

      setSnackbarMessage('Employee details updated successfully!');
      setSnackbarSeverity('success');
      setIsPersonalEditable(false);
      setIsAddressEditable(false);
    } catch (error) {
      setSnackbarMessage('Failed to update employee details.');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsPersonalEditable(false);
    setIsAddressEditable(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '72vh' }}>
        <CircularProgress size={72} />
      </Box>
    );
  }

  const canEditPersonal = isPersonalEditable || isAdmin;
  const canEditAddress = isAddressEditable || isAdmin;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: { xs: 1.5, md: 2.5 },
          minHeight: '100%',
          background: 'background.default',
        }}
      >
        <Box sx={{ maxWidth: 980, mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 2.6 },
                background: 'background.paper',
                color: 'text.primary',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar
                  sx={{ width: 72, height: 72, border: '2px solid rgba(15,23,42,0.08)' }}
                  src={employee.imageUrl || ''}
                  alt="Employee Avatar"
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {employee.firstName || '-'} {employee.lastName || ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3 }}>
                    {employee.email || 'No email'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<Badge sx={{ color: 'text.secondary' }} fontSize="small" />}
                      label={`Code: ${employee.employeeCode || '-'}`}
                      size="small"
                      sx={{ bgcolor: alpha('#0F172A', 0.06), color: 'text.primary', borderRadius: 1.5 }}
                    />
                    <Chip
                      icon={<WorkOutline sx={{ color: 'text.secondary' }} fontSize="small" />}
                      label={employee.role || 'EMPLOYEE'}
                      size="small"
                      sx={{ bgcolor: alpha('#0F172A', 0.06), color: 'text.primary', borderRadius: 1.5 }}
                    />
                    <Chip
                      label={employee.status || 'Active'}
                      size="small"
                      sx={{ bgcolor: alpha('#0F172A', 0.06), color: 'text.primary', borderRadius: 1.5 }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', backgroundColor: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                      <SectionTitle
                        title="Personal Information"
                        subtitle="Basic profile and contact details"
                        editable={isPersonalEditable || isAdmin}
                        onEdit={!isAdmin ? () => setIsPersonalEditable(true) : null}
                      />
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="First Name" name="firstName" value={employee.firstName || ''} onChange={handleInputChange} disabled={!canEditPersonal} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Last Name" name="lastName" value={employee.lastName || ''} onChange={handleInputChange} disabled={!canEditPersonal} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Email" name="email" value={employee.email || ''} onChange={handleInputChange} disabled={!canEditPersonal} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Phone" name="phone" value={employee.phone || ''} onChange={handleInputChange} disabled={!canEditPersonal} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Date of Birth"
                            name="dateOfBirth"
                            type="date"
                            value={employee.dateOfBirth || ''}
                            onChange={handleInputChange}
                            disabled={!canEditPersonal}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small" disabled={!canEditPersonal}>
                            <InputLabel>Gender</InputLabel>
                            <Select label="Gender" name="gender" value={employee.gender || ''} onChange={handleInputChange}>
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth size="small" label="Qualification" name="qualification" value={employee.qualification || ''} onChange={handleInputChange} disabled={!canEditPersonal} />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                  <Card variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', backgroundColor: 'transparent', boxShadow: 'none' }}>
                    <CardContent>
                        <SectionTitle
                          title="Address & Contacts"
                          subtitle="Address and emergency info"
                          editable={isAddressEditable || isAdmin}
                          onEdit={!isAdmin ? () => setIsAddressEditable(true) : null}
                        />
                        <Grid container spacing={1.5}>
                          <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Address" name="address" value={employee.address || ''} onChange={handleInputChange} disabled={!canEditAddress} multiline minRows={2} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Blood Group" name="bloodGroup" value={employee.bloodGroup || ''} onChange={handleInputChange} disabled={!canEditAddress} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Emergency Contact" name="emergencyContact" value={employee.emergencyContact || ''} onChange={handleInputChange} disabled={!canEditAddress} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', backgroundColor: 'transparent', boxShadow: 'none' }}>
                      <CardContent>
                        <SectionTitle title="Job Information" subtitle="Admin-managed employment details" editable={isAdmin} />
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Salary" name="salary" value={employee.salary || ''} onChange={handleInputChange} disabled={!isAdmin} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label="Date of Joining"
                              value={employee.dateOfJoining}
                              onChange={handleDateOfJoiningChange}
                              inputFormat="DD MMM YYYY"
                              renderInput={(props) => <TextField {...props} fullWidth size="small" />}
                              disabled={!isAdmin}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Subjects Taught" name="subjectsTaught" value={employee.subjectsTaught || ''} onChange={handleInputChange} disabled={!isAdmin} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Experience" name="experience" value={employee.experience || ''} onChange={handleInputChange} disabled={!isAdmin} />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.2, position: 'sticky', bottom: 0, pt: 1, bgcolor: 'background.paper' }}>
                {(isPersonalEditable || isAddressEditable || isAdmin) && (
                  <>
                    <Button
                      variant="outlined"
                      color="inherit"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Paper>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EmployeeEditComponent;
